import { RedDotListData, TmapApp } from '@tmap-web-lib/tmap-app-interface'
import { formatCommaNumber, isLocalEnv } from '@tmap-web-lib/utils'
import classNames from 'classnames'
import dayjs from 'dayjs'
import { motion } from 'framer-motion'
import { useAtom, useAtomValue } from 'jotai'
import { debounce } from 'lodash-es'
import Image from 'next/image'
import { useCallback, useEffect, useMemo, useRef } from 'react'

import imgCurrentPosition from '@/assets/images/img-current-position.svg'
import IconCoupon from '@/assets/tmds-icons/ic_coupon.svg?component'
import IconStore from '@/assets/tmds-icons/ic_store.svg?component'
import { globalState, storagePointTooltipClicked } from '@/atoms'
import Tooltip from '@/components/Tooltip/Tooltip'
import { sendEventLog } from '@/features/log/log.fn'
import MyLogDefinition from '@/features/log/log.my'
import { VerticalRollingMessage } from '@/features/web/components/point/VerticalRollingMessage'
import { Box } from '@/features/web/move_log/components/box'
import { Button } from '@/features/web/move_log/components/button'
import { Flex } from '@/features/web/move_log/components/flex'
import { Text } from '@/features/web/move_log/components/text'
import { Vsm } from '@/features/web/move_log/components/vsm'
import { useCurrentPosition } from '@/features/web/move_log/hooks/use-current-position'
import { useOpenService } from '@/hooks'

import * as styles from './Point.css'

import { sprinkles } from '@/styles/sprinkles.css'

export type MileageGradeType = 'BRONZE' | 'SILVER' | 'GOLD' | 'VIP'

export interface UserPointProps {
  point: number
  pointRedDotList: RedDotListData[]
  mileage: number
  grade: MileageGradeType
  isEventOngoing: boolean
  text: string
  usableCouponCount: number
  usableBizCouponCount: number
  bizCouponRedDotList: RedDotListData[]
  poiVisitedCount?: number
}

function Point(props: UserPointProps) {
  const {
    point,
    mileage,
    grade,
    isEventOngoing,
    text,
    usableCouponCount,
    usableBizCouponCount,
    pointRedDotList,
    bizCouponRedDotList,
    poiVisitedCount
  } = props
  const globalStateValue = useAtomValue(globalState)
  const pointText = globalStateValue.MY_POINT_TEXT ?? { text: '' }
  const mileageText = globalStateValue.MY_MILEAGE_TEXT ?? { text: '' }
  const isShowMoveLog = useMemo(() => {
    return isLocalEnv() || globalStateValue.MY_MOVELOG_RELEASE_DATE != null
  }, [globalStateValue.MY_MOVELOG_RELEASE_DATE])
  const { action_id } = MyLogDefinition.my
  const openService = useOpenService()
  const [isPointTooltipClosed, setPointTooltipClosed] = useAtom(storagePointTooltipClicked)

  const isPointTooltipClosedRef = useRef(isPointTooltipClosed)

  const [position, refreshPosition] = useCurrentPosition()
  const handleClickMoveLogMap = () => {
    sendEventLog('tap.moving_log', {
      log_yn: poiVisitedCount ? 'y' : 'n',
    })
    openService('tmap://vertical?service=move-log&extra=%7B%22logbox%22%3A%7B%22type%22%3A%22tab%22%2C%22origin%22%3A%22my%22%7D%7D')
  }

  const callMoveToPointDebouncing = useMemo(
    () =>
      debounce(
        () => {
          if (pointRedDotList.length) {
            TmapApp.updateRedDotList({
              updateData: [
                {
                  itemGroup: 'SERVICE_INDEX',
                  item: 'POINT',
                  displayType: 'REDDOT',
                  readDateTime: dayjs().format('YYYYMMDDHHmmss'),
                },
              ],
            })
            TmapApp.updateAllRedDots()
          }
          setPointTooltipClosed(true)
          sendEventLog(action_id?.tapPoint, {
            point_amount: point,
            unit: pointRedDotList.length > 0 ? 'REDDOT' : null,
            nudge_text: pointText.text.length ? pointText.text : null,
            tooltip: !isPointTooltipClosedRef.current ? 'y' : 'n',
          })
          openService('tmap://paypoint?pageid=point')
        },
        1000,
        { leading: true, trailing: false },
      ),
    [
      pointRedDotList,
      openService,
      action_id?.tapPoint,
      point,
      pointText.text,
      setPointTooltipClosed,
    ],
  )

  const handleClickPoint = useCallback(() => {
    callMoveToPointDebouncing()
  }, [callMoveToPointDebouncing])

  const callMoveToMileageDebouncing = useMemo(
    () =>
      debounce(
        () => {
          sendEventLog(action_id?.tapMileage, {
            mileage_amount: mileage > 0 ? 'y' : 'n',
            nudge_text: mileageText.text.length ? mileageText.text : null,
            badge: grade,
            event: isEventOngoing ? 'y' : 'n',
          })
          openService('tmap://paypoint?pageid=mileage')
        },
        1000,
        { leading: true, trailing: false },
      ),
    [openService, action_id?.tapMileage, grade, isEventOngoing, mileage, mileageText.text],
  )

  const handleClickMileage = useCallback(() => {
    callMoveToMileageDebouncing()
  }, [callMoveToMileageDebouncing])

  const callMoveToCouponDebouncing = useMemo(
    () =>
      debounce(
        () => {
          sendEventLog(action_id?.tapCoupon, {
            coupon_amount: usableCouponCount > 0 ? 'y' : 'n',
          })
          openService('tmap://promotion')
        },
        1000,
        { leading: true, trailing: false },
      ),
    [openService, action_id?.tapCoupon, usableCouponCount],
  )

  const handleCouponClick = useCallback(() => {
    callMoveToCouponDebouncing()
  }, [callMoveToCouponDebouncing])

  const callMoveToBizCouponDebouncing = useMemo(
    () =>
      debounce(
        () => {
          if (bizCouponRedDotList.length) {
            TmapApp.updateRedDotList({
              updateData: [
                {
                  itemGroup: 'SERVICE_INDEX',
                  item: 'POI_COUPON',
                  displayType: 'REDDOT',
                  readDateTime: dayjs().format('YYYYMMDDHHmmss'),
                },
              ],
            })
            TmapApp.updateAllRedDots()
          }
          sendEventLog(action_id?.tapPoiCoupon, {
            coupon_amount: usableBizCouponCount > 0 ? 'y' : 'n',
          })
          openService('tmap://vertical?service=couponList')
        },
        1000,
        {
          leading: true,
          trailing: false,
        },
      ),
    [bizCouponRedDotList, openService, action_id?.tapPoiCoupon, usableBizCouponCount],
  )

  const handleBizCouponClick = useCallback(() => {
    callMoveToBizCouponDebouncing()
  }, [callMoveToBizCouponDebouncing])

  useEffect(() => {
    isPointTooltipClosedRef.current = isPointTooltipClosed
  }, [isPointTooltipClosed])

  useEffect(() => {
    sendEventLog(action_id?.viewPoiCoupon, {
      coupon_amount: usableBizCouponCount > 0 ? 'y' : 'n',
    }, undefined, 'mixpanel')
  }, [action_id?.viewPoiCoupon, usableBizCouponCount])

  useEffect(() => {
    sendEventLog(action_id?.viewCoupon, {
      coupon_amount: usableCouponCount > 0 ? 'y' : 'n',
    }, undefined, 'mixpanel')
  }, [action_id?.viewCoupon, usableCouponCount])

  useEffect(() => {
    return TmapApp.utils.addNativeEventListener('onResume', () => {
      refreshPosition()
    })
  }, [refreshPosition])

  // useEffect(() => {
  //   sendEventLog(action_id?.viewDgtourcard, {
  //     tooltip: !isDgTourTooltipClosedRef.current ? 'y' : 'n',
  //   })
  // }, [action_id?.viewDgtourcard])

  return (
    <>
      <div className={styles.wrapper}>
        <div className={styles.rewardWrapper}>
          <div className={styles.pointWrapper}>
            <Tooltip
              storageKey={storagePointTooltipClicked}
              content={'포인트샵이 오픈했어요'}
              layout={{
                bubbleOffsetX: '12px',
                bubbleOffsetY: '-26px',
                arrowOffsetY: '-12px',
                arrowDirection: 'BOTTOM',
              }}
            />
            <button
              className={styles.pointComponent}
              onClick={handleClickPoint}
            >
              <p
                className={`${styles.title} ${pointRedDotList.length ? styles.redDotVariants.reddot : ''}`}
              >
                티맵 포인트
              </p>
              {point === 0 ? (
                pointText.text.length ? (
                  <div
                    className={classNames(styles.baseMessageStyle, styles.messageVariants.point)}
                  >
                    <p className={styles.messageText}>{pointText.text}</p>
                  </div>
                ) : (
                  <div className={styles.valueWrapper}>
                    <span className={styles.value}>0원</span>
                    <Image src={require('../../../../../public/assets/images/ico_arrow_right.svg').default} className={styles.arrowIcon} alt="" />
                  </div>
                )
              ) : (
                <div className={styles.valueWrapper}>
                  <span className={styles.value}>{formatCommaNumber(point)}원</span>
                  <Image src={require('../../../../../public/assets/images/ico_arrow_right.svg').default} className={styles.arrowIcon} alt="" />
                </div>
              )}
            </button>
            <div className={styles.verticalLine} />
            <button
              className={styles.mileageComponent}
              onClick={handleClickMileage}
            >
              <div className={styles.mileageMessage}>
                <p className={styles.title}>대리 마일리지</p>
                <VerticalRollingMessage
                  grade={grade}
                  isEventOngoing={isEventOngoing}
                  text={text}
                />
              </div>
              {mileage === 0 ? (
                mileageText.text.length ? (
                  <div
                    className={classNames(styles.baseMessageStyle, styles.messageVariants.mileage)}
                  >
                    <p className={styles.messageText}>{mileageText.text}</p>
                  </div>
                ) : (
                  <div className={styles.valueWrapper}>
                    <span className={styles.value}>0M</span>
                    <Image src={require('../../../../../public/assets/images/ico_arrow_right.svg').default} className={styles.arrowIcon} alt="" />
                  </div>
                )
              ) : (
                <div className={styles.valueWrapper}>
                  <span className={styles.value}>{formatCommaNumber(mileage)}M</span>
                  <Image src={require('../../../../../public/assets/images/ico_arrow_right.svg').default} className={styles.arrowIcon} alt="" />
                </div>
              )}
            </button>
          </div>
        </div>

        {/*{dgTourCard?.isShow && gte(TmapApp.env.appVersion, '10.10.0') && (*/}
        {/*  <div className={styles.dgTourCardWrapper}>*/}
        {/*    <button*/}
        {/*      className={styles.dgTourCardContainer}*/}
        {/*      onClick={handleDgTourCardClick}*/}
        {/*    >*/}
        {/*      <span className={styles.dgTourCardContent}>*/}
        {/*        <Image*/}
        {/*          style={{ marginRight: '6px' }}*/}
        {/*          src={require('../../../../../public/assets/images/qr_code.svg').default}*/}
        {/*          alt=""*/}
        {/*        />*/}
        {/*        디지털 관광주민증*/}
        {/*        <Image*/}
        {/*          style={{ marginLeft: 'auto' }}*/}
        {/*          src={require('../../../../../public/assets/images/ico_arrow_right.svg').default}*/}
        {/*          alt=""*/}
        {/*        />*/}
        {/*      </span>*/}
        {/*    </button>*/}
        {/*    {isPointTooltipClosed && (*/}
        {/*      <Tooltip*/}
        {/*        storageKey={storageDgTourCardTooltipClicked}*/}
        {/*        content={'국내여행 할인받기'}*/}
        {/*        layout={{*/}
        {/*          bubbleOffsetX: '11px',*/}
        {/*          arrowOffsetX: '29px',*/}
        {/*        }}*/}
        {/*      />*/}
        {/*    )}*/}
        {/*  </div>*/}
        {/*)}*/}
      </div>
      {!isShowMoveLog
        ? (
          <div className={styles.wrapper}>
            <div className={styles.rewardWrapper}>
              <div className={styles.pointWrapper}>
                <button
                  className={styles.bizCouponComponent}
                  onClick={handleBizCouponClick}
                >
                  <Image
                    style={{ marginRight: '6px' }}
                    src={require('../../../../../public/assets/images/ico_store.svg').default}
                    alt=""
                  />
                  <span className={styles.title}>매장 쿠폰</span>
                  <span
                    className={`${styles.couponValue} ${bizCouponRedDotList.length ? styles.bizCouponRedDotVariants.reddot : ''}`}
                  >
                    {formatCommaNumber(usableBizCouponCount)}
                  </span>
                </button>
                <div className={styles.verticalLine} />
                <button
                  className={styles.couponComponent}
                  onClick={handleCouponClick}
                >
                  <Image
                    style={{ marginRight: '6px' }}
                    src={require('../../../../../public/assets/images/ico_coupon.svg').default}
                    alt=""
                  />
                  <span className={styles.title}>제휴 쿠폰</span>
                  <span className={styles.couponValue}>{formatCommaNumber(usableCouponCount)}</span>
                </button>
              </div>
            </div>
          </div>
        )
        : (
          <Flex mx={'spacing250'} my={'spacing150'} gap={'spacing150'}>
            <Box className={styles.wrapper} w={'50%'} style={{ margin: 0 }}>
              <Box className={styles.rewardWrapper}>
                <Box className={styles.pointWrapper}>
                  <Flex flexDirection={'column'} w={'100%'}>
                    <Button
                      variant={'ghost'}
                      size={'medium'}
                      onClick={handleBizCouponClick}
                      className={styles.blockButtonRecipe({ position: 'top' })}
                    >
                      <IconStore width={22} height={22} className={sprinkles({ flexShrink: 0 })} />
                      <Text fontSize={'body3'} fontWeight={'medium'} whiteSpace={'nowrap'}>매장 쿠폰</Text>
                      <Flex alignItems={'center'} position={'relative'} minW={0} h={'100%'}>
                        <Text
                          fontSize={usableBizCouponCount ? 'body1' : 'body4'}
                          fontWeight={usableBizCouponCount ? 'bold' : 'medium'}
                          color={usableBizCouponCount ? 'textBasicDefault' : 'textBasicCaption'}
                          ellipsis
                          className={styles.hide320ScreenCss}
                        >{formatCommaNumber(usableBizCouponCount)}</Text>
                        {bizCouponRedDotList.length
                          ? <Box h={'100%'} className={styles.storeCouponCountRedDotCss}/>
                          : null
                        }
                      </Flex>
                    </Button>
                    <Flex>
                      <Box
                        bg={'borderLineDefault'}
                        w={'100%'}
                        mx={'spacing225'}
                        style={{ height: '1px' }}
                      />
                    </Flex>
                    <Button
                      variant={'ghost'}
                      size={'medium'}
                      onClick={handleCouponClick}
                      className={styles.blockButtonRecipe({ position: 'bottom' })}
                    >
                      <IconCoupon width={22} height={22} className={sprinkles({ flexShrink: 0 })} />
                      <Text fontSize={'body3'} fontWeight={'medium'} whiteSpace={'nowrap'}>제휴 쿠폰</Text>
                      <Text
                        fontSize={usableCouponCount ? 'body1' : 'body4'}
                        fontWeight={usableCouponCount ? 'bold' : 'medium'}
                        color={usableCouponCount ? 'textBasicDefault' : 'textBasicCaption'}
                        ellipsis
                        className={styles.hide320ScreenCss}
                      >{formatCommaNumber(usableCouponCount)}</Text>
                    </Button>
                  </Flex>
                </Box>
              </Box>
            </Box>
            <Button
              variant={'ghost'}
              className={styles.moveLogButtonCss}
              onClick={handleClickMoveLogMap}
            >
              <Vsm
                minZoom={5}
                maxZoom={20}
                interactive={false}
                position={position ? { center: [position.longitude, position.latitude], zoom: 14 } : undefined}
                className={sprinkles({ pointerEvents: 'none' })}
              />
              <Flex
                position={'absolute'}
                alignItems={'center'}
                gap={'spacing75'}
                borderRadius={'radius100'}
                background={'paletteWhiteAlphaHigh'}
                px={'spacing150'}
                py={'spacing100'}
                className={styles.moveLogMapTextBlockRecipe({ position: 'top' })}
              >
                <Flex
                  justifyContent={'center'}
                  alignItems={'center'}
                  className={styles.moveLogDotPointBeforeTextRecipe({ position: 'out' })}
                >
                  <Box className={styles.moveLogDotPointBeforeTextRecipe({ position: 'in' })} />
                </Flex>
                <Text fontSize={'body4'} fontWeight={'bold'} lineHeight={'body4'}>내 이동로그</Text>
              </Flex>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: poiVisitedCount != null ? 1 : 0 }}
                className={classNames(
                  sprinkles({
                    position:'absolute',
                    display: 'flex',
                    alignItems:'center',
                    gap:'spacing75',
                    borderRadius:'radius100',
                    background:'paletteWhiteAlphaHigh',
                    px:'spacing150',
                    py:'spacing100',
                  }),
                  styles.moveLogMapTextBlockRecipe({ position: 'bottom' }),
                )}
              >
                <Text inline fontSize={'body4'} fontWeight={'medium'} lineHeight={'body4'} className={styles.hide320ScreenCss}>최근 1년</Text>
                <Text inline fontSize={'body4'} fontWeight={'bold'} lineHeight={'body4'}>
                  {poiVisitedCount != null && poiVisitedCount >= 999 ? '999+' : poiVisitedCount}곳 방문
                </Text>
              </motion.div>
              <img
                src={imgCurrentPosition.src}
                alt={''}
                width={44}
                height={44}
                className={styles.moveLogCurrentPositionMarkerCss}
              />
            </Button>
          </Flex>
        )
      }
    </>
  )
}

export { Point }
